/**
 * 라우팅 루트 및 인증 복원
 * ---------------------------------------------------------------------------
 * 새로고침하면 Access Token(메모리)이 사라지므로, 앱 시작 시 refresh 쿠키로
 * 세션을 복구한다. 복구에 실패하면 로그인 화면으로 보낸다.
 *
 * 라우트 가드는 UX 목적이다. 실제 차단은 서버가 수행한다.
 */

import {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Alert, App as AntdApp, Button, Input, Layout, Menu, Result, Spin, Tag, Typography } from 'antd';
// 메뉴 아이콘은 lib/menu.tsx 가 가지고 있다. 여기서는 지원 모드 배너만 쓴다.
import { SafetyCertificateOutlined, SearchOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, renewSupportSession, setAccessToken } from './lib/api-client';
import {
  getSupportSession,
  hasSupportHint,
  remainingText,
  setSupportSession,
  subscribeSupportSession,
} from './lib/support-mode';
import {
  clearCompanyLogo,
  getCompanyLogo,
  loadCompanyLogo,
  subscribeCompanyLogo,
} from './lib/company-logo';
import { AuthContext, useAuth, type AuthProfile, type AuthState } from './lib/auth-store';
import LoginPage from './features/auth/LoginPage';
import InvitePage from './features/auth/InvitePage';
import SsoBridgePage from './features/auth/SsoBridgePage';
import { PublicInvoicePage } from './features/billing/PublicInvoicePage';
import UserListPage from './features/users/UserListPage';
import DepartmentPage from './features/departments/DepartmentPage';
import TenantSettingsPage from './features/tenant/TenantSettingsPage';
import AccountPage from './features/account/AccountPage';
import AdminTenantPage from './features/admin/AdminTenantPage';
import DashboardPage from './features/admin/DashboardPage';
import PlanPage from './features/admin/PlanPage';
import ProductPage from './features/admin/ProductPage';
import AdminBillingPage from './features/admin/BillingPage';
import TenantBillingPage from './features/billing/BillingPage';
import ProductStorePage from './features/billing/ProductStorePage';
import TenantDashboardPage from './features/tenant/TenantDashboardPage';
import AiReportArchivePage from './features/tenant/AiReportArchivePage';
import LogsPage from './features/logs/LogsPage';
import CodePage from './features/admin/CodePage';
import AiSettingPage from './features/admin/AiSettingPage';
import { MailSettingPage } from './features/admin/MailSettingPage';
import { MessageTemplatePage } from './features/admin/MessageTemplatePage';
import RevenuePage from './features/admin/RevenuePage';
import PaymentPage from './features/admin/PaymentPage';
import IntegrationPage from './features/admin/IntegrationPage';
import FooterPage from './features/admin/FooterPage';
import SystemStatusPage from './features/admin/SystemStatusPage';
import TenantUsagePage from './features/admin/TenantUsagePage';
import { AppFooter } from './components/AppFooter';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageHelp } from './components/PageHelp';
import { GUIDE, guideKeyFromPath } from './lib/guide';
import RolePage from './features/roles/RolePage';
import { buildMenu, homeKeyFor, openKeysFor } from './lib/menu';
import MenuSearch from './components/MenuSearch';
import { useOnboarding, trialLabel, trialLevel } from './features/tenant/onboarding';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

/**
 * 새로고침 시 세션을 복구한다. **호출이 겹쳐도 한 번만 실행된다.**
 *
 * StrictMode 는 개발 중에 효과를 두 번 실행한다. 효과 안에서 직접 부르면
 * /auth/refresh 가 같은 쿠키로 두 번 나가고, refresh 토큰은 1회용이라
 * 두 번째가 재사용으로 걸려 **전 세션이 끊긴다.** 새 탭을 열 때마다 로그인이
 * 풀린 원인이었다.
 *
 * 서버에도 짧은 유예를 두었지만(REFRESH_REUSE_GRACE_SECONDS), 애초에 두 번
 * 부르지 않는 것이 맞다. 결과를 약속에 담아 두 번째 마운트가 그대로 받는다.
 */
let restorePromise: Promise<AuthProfile | null> | null = null;

function restoreSession(): Promise<AuthProfile | null> {
  restorePromise ??= (async () => {
    // 서버가 로그인 시 심어 둔 표시 쿠키가 없으면 세션이 없다는 뜻이다.
    // 확인 없이 /auth/refresh 를 부르면 비로그인 사용자에게 매번 401 이 찍힌다.
    if (!hasSessionHint()) return null;

    try {
      // 지원 모드 중에 새로고침했다면 지원 세션부터 복구한다.
      // 일반 세션으로 먼저 복구하면 고객사 화면을 띄운 채 토큰만
      // 운영자 소속 기업으로 바뀌어 아무것도 보이지 않는다.
      const restored = hasSupportHint() ? await renewSupportSession() : null;

      if (!restored) {
        const { data } = await apiClient.post<{ accessToken: string }>('/auth/refresh');
        setAccessToken(data.accessToken);
      }

      const me = await apiClient.get<AuthProfile>('/auth/me');
      return me.data;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      // 다음 새로고침에서는 다시 복구해야 한다.
      restorePromise = null;
    }
  })();

  return restorePromise;
}

export default function App() {
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  useEffect(() => {
    let cancelled = false;

    void restoreSession().then((restored) => {
      if (cancelled) return;
      setProfile(restored);
      setStatus(restored ? 'authenticated' : 'anonymous');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const auth = useMemo<AuthState>(
    () => ({
      profile,
      status,
      setProfile: (next) => {
        setProfile(next);
        setStatus(next ? 'authenticated' : 'anonymous');
      },
      signOut: async () => {
        await apiClient.post('/auth/logout').catch(() => undefined);
        setSupportSession(null);
        // 다음에 다른 기업으로 로그인할 수 있다. 옛 로고가 남아 있으면
        // 잠깐이라도 남의 회사 표지가 머리말에 뜬다.
        clearCompanyLogo();
        setAccessToken(null);
        setProfile(null);
        setStatus('anonymous');
      },
    }),
    [profile, status],
  );

  if (status === 'loading') return <LoadingScreen />;

  return (
    <AuthContext.Provider value={auth}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* 초대 수락(최초 비밀번호 설정) — 인증 없이 접근한다 */}
        <Route path="/invite" element={<InvitePage />} />

        {/*
          청구서 열람 — 메일의 링크로 들어온다. 받는 사람은 보통 경리
          담당자라 Hub 계정이 없다. 로그인 대신 **사업자등록번호**를 묻는다
          (링크는 난수라, 링크와 번호 둘 다 알아야 열린다).
        */}
        <Route path="/invoice/:token" element={<PublicInvoicePage />} />

        {/*
          제품으로 건너가는 다리. 로그인이 필요하므로 RequireAuth 아래 둔다 —
          비로그인이면 /login?next=/sso?... 로 보냈다가 돌아온다.
        */}
        <Route
          path="/sso"
          element={
            <RequireAuth>
              <SsoBridgePage />
            </RequireAuth>
          }
        />

        <Route
          path="/app/*"
          element={
            <RequireAuth>
              <HubConsoleGate>
                <AppShell />
              </HubConsoleGate>
            </RequireAuth>
          }
        />

        <Route path="/" element={<Navigate to="/app/users" replace />} />
        <Route
          path="*"
          element={<Result status="404" title="404" subTitle="페이지를 찾을 수 없습니다." />}
        />
      </Routes>
    </AuthContext.Provider>
  );
}

/**
 * WORK&Hub 콘솔은 기업 관리자만 들어온다.
 *
 * ## 왜 로그인에서 막지 않나
 * 제품(WORK&ECO·WORK&CRM)은 **Hub 로그인 화면을 거쳐** SSO 한다 —
 * 제품 → /sso?product=eco → (로그인) → 1회용 코드 → 제품. 로그인을
 * 막으면 제품 로그인까지 막힌다. 그래서 로그인과 /sso 는 열어 두고
 * **콘솔 화면만** 닫는다. 서버도 같은 판정을 한다(HubConsoleOnly).
 *
 * ## 「권한이 없습니다」로 끝내지 않는다
 * 그렇게만 적으면 사용자는 비밀번호가 틀린 줄 알고 다시 로그인하고,
 * 몇 번 틀리면 계정이 잠긴다. **어디로 가야 하는지**를 단추로 준다.
 */
function HubConsoleGate({ children }: { children: ReactNode }) {
  const { profile, signOut } = useAuth();
  const roles = profile?.roles ?? [];
  const allowed =
    roles.includes('TENANT_OWNER') ||
    roles.includes('TENANT_ADMIN') ||
    roles.includes('PLATFORM_ADMIN');

  if (allowed) return <>{children}</>;

  /*
   * 제품 주소는 빌드 설정에서 온다. 없으면 단추를 그리지 않는다 —
   * 눌러도 아무 데도 가지 않는 단추보다 없는 편이 낫다.
   */
  const origins = (import.meta.env.VITE_PRODUCT_ORIGINS ?? '')
    .split(',')
    .map((o: string) => o.trim())
    .filter(Boolean);

  return (
    <Result
      status="info"
      title="WORK&Hub 는 기업 관리자용입니다"
      subTitle={
        <span>
          로그인은 정상입니다 — 이 화면만 기업 소유자·관리자에게 열려 있습니다.
          <br />
          쓰시던 제품으로 들어가 주세요. 내 정보와 비밀번호는 각 제품의{' '}
          <b>「내 정보」</b> 화면에서 바꾸실 수 있습니다.
        </span>
      }
      extra={[
        ...origins.map((origin: string) => (
          <Button key={origin} type="primary" href={origin}>
            {origin.replace(/^https?:\/\//, '')} 으로 가기
          </Button>
        )),
        <Button key="logout" onClick={() => { void signOut(); }}>
          로그아웃
        </Button>,
      ]}
    />
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { status } = useAuth();

  if (status === 'loading') return <LoadingScreen />;
  if (status !== 'authenticated') {
    // search 를 함께 넘긴다. /sso?product=eco&next=... 처럼 쿼리에 의미가 있는
    // 경로가 있어서, pathname 만 넘기면 로그인 뒤 돌아왔을 때 조건이 사라진다.
    const back = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(back)}`} replace />;
  }
  return <>{children}</>;
}

function AppShell() {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const support = useSupportSession();
  const companyLogo = useCompanyLogo();

  /*
   * 기업 로고를 받아 둔다.
   *
   * 지원 모드가 켜지거나 꺼지면 보고 있는 기업이 바뀐다. 그때 다시 받지
   * 않으면 고객사 화면에 운영자 회사의 로고가 걸린 채로 남는다.
   */
  useEffect(() => {
    void loadCompanyLogo();
  }, [support?.tenantId]);

  // 플랫폼 운영자만 기업 관리 메뉴를 본다.
  // 화면 숨김은 UX 일 뿐이고, 실제 차단은 서버 가드가 한다.
  //
  // roles 까지 물음표를 붙인다. profile 만 막으면 roles 가 빠진 응답(304 캐시,
  // 옛 버전 API)에서 `.includes` 가 터지고, 울타리 밖이라 화면이 하얗게 된다.
  const isPlatformAdmin = profile?.roles?.includes('PLATFORM_ADMIN') ?? false;

  const navigate = useNavigate();

  // permissions 는 아직 못 받았을 수 있다. 그때는 undefined 로 둔다 —
  // menu.tsx 가 "모름" 과 "권한 없음" 을 다르게 다룬다(메뉴 깜빡임 방지).
  const menuContext = {
    isPlatformAdmin,
    support: Boolean(support),
    permissions: profile?.permissions,
  };
  const home = homeKeyFor(menuContext);

  /*
   * 무료 기간 딱지와 로그인 직후 안내.
   *
   * 통합관리자에게는 묻지 않는다 — 운영자의 기업(플랫폼 테넌트)은 체험도
   * 결제 수단도 해당이 없고, 「사업자등록증을 올리세요」가 뜨면 그것부터
   * 틀린 안내다.
   */
  const asTenant = !isPlatformAdmin || Boolean(support);
  const onboarding = useOnboarding(asTenant && Boolean(profile));
  const trialText = trialLabel(onboarding.data?.trial);
  const trialTone = { ok: 'blue', warning: 'orange', danger: 'red' }[
    trialLevel(onboarding.data?.trial)
  ];

  // 통합관리자 화면을 보여도 되는가. 메뉴(buildMenu)와 **같은 식**이어야 한다.
  // 둘이 어긋나면 메뉴에는 있는데 라우트가 없어 빈 화면이 된다.
  const asPlatform = isPlatformAdmin && !support;

  // 현재 메뉴는 /app 바로 다음 세그먼트로 판단한다.
  // includes() 로 검사하면 경로에 여러 키워드가 섞일 때 잘못 매칭된다.
  const selected = location.pathname.split('/').filter(Boolean)[1] ?? home;

  /*
   * 지금 화면이 속한 묶음을 펼친다.
   *
   * defaultOpenKeys 가 아니라 openKeys 를 쓰되 사용자가 접은 것도 기억한다 —
   * 매번 강제로 펼치면 접을 수가 없고, 처음 값만 주면 링크로 다른 묶음의
   * 화면에 들어왔을 때 접힌 채로 남는다.
   */
  const [openKeys, setOpenKeys] = useState<string[]>(() => openKeysFor(selected, menuContext));
  const [touchedKeys, setTouchedKeys] = useState(false);

  /*
   * 메뉴 검색 (Ctrl+K / Cmd+K). WORK&CRM 과 같은 조작이다.
   *
   * 브라우저 기본 동작(Chrome 의 주소창 검색)을 막아야 하므로 preventDefault
   * 를 건다. 입력 중에도 받는다 — 글을 쓰다가 다른 화면으로 건너뛰는 것이
   * 이 단축키의 쓸모라서, 입력칸 안이라고 막으면 쓸 수 없는 자리가 생긴다.
   */
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (touchedKeys) return;
    setOpenKeys(openKeysFor(selected, menuContext));
    // menuContext 는 매번 새 객체라 의존성에 넣지 않는다. 값이 바뀌면 selected
    // 나 지원 모드가 같이 바뀐다. 권한은 프로필이 늦게 와서 메뉴가 나중에
    // 좁혀지므로 길이로 함께 본다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, isPlatformAdmin, support, touchedKeys, profile?.permissions?.length]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingInline: 24,
        }}
      >
        {/*
          CI. 로고가 받아지지 않아도 제품 이름은 남아야 하므로 alt 를 제품명으로 둔다.
        */}
        <img
          src="/worknhub-logo.png"
          alt="WORKnHub"
          height={30}
          style={{ display: 'block', flexShrink: 0 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {/*
            무료 사용 기간 딱지.

            회사 이름 **왼쪽**에 둔다 — 오른쪽 끝은 로그아웃 자리라 누르려다
            잘못 누르기 쉽다. 남은 날이 3일 이하면 붉게, 7일 이하면 주황으로
            바뀌지만 **문구에 날짜가 이미 들어 있어** 색을 못 봐도 읽힌다.

            남은 날은 서버가 세 준다. 화면이 세면 표준시간대와 경계 처리가
            갈려 **화면은 3일, 메일은 5일**이 된다.
          */}
          {trialText && (
            <Tag
              color={trialTone}
              style={{ marginInlineEnd: 0, fontWeight: 600 }}
              onClick={() => navigate('/app/billing')}
            >
              {trialText}
            </Tag>
          )}
          {/*
            기업 로고는 **제품 로고 옆이 아니라 회사 이름 옆**에 둔다.
            왼쪽은 "지금 쓰고 있는 제품", 오른쪽은 "지금 보고 있는 회사" 로
            자리의 뜻이 갈려야 둘이 섞여 보이지 않는다.

            등록하지 않았으면 아무것도 그리지 않는다 — 빈 네모를 두면
            "그림이 깨졌다" 로 읽힌다.
          */}
          {companyLogo && (
            <img
              src={companyLogo}
              alt={profile?.companyName ?? '기업 로고'}
              style={{ display: 'block', height: 28, maxWidth: 160, objectFit: 'contain' }}
            />
          )}
          <Text style={{ color: 'rgba(255,255,255,0.75)' }}>
            {profile?.companyName} · {profile?.name}
            <a style={{ marginLeft: 16, color: '#fff' }} onClick={() => void signOut()}>
              로그아웃
            </a>
          </Text>
        </div>
      </Header>

      {support && <SupportBanner />}

      {/*
        메뉴 검색. 열려 있을 때만 그린다 — 검색어·커서를 비우는 코드가
        따로 필요 없고, 닫혀 있는 동안 키 입력을 듣지도 않는다.
      */}
      {searchOpen && (
        <MenuSearch
          open
          ctx={menuContext}
          onClose={() => setSearchOpen(false)}
          onPick={(key) => navigate(`/app/${key}`)}
        />
      )}

      <Layout>
        <Sider width={200} theme="light">
          {/*
            검색 단추. 단축키만 두면 아무도 모른다 — 눌러서도 열리게 하고,
            단축키를 그 자리에 적어 두면 다음부터는 손이 먼저 간다.
          */}
          <div style={{ padding: '12px 12px 4px' }}>
            <Input
              readOnly
              onClick={() => setSearchOpen(true)}
              placeholder="메뉴 검색"
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              suffix={<Text type="secondary" style={{ fontSize: 11 }}>Ctrl K</Text>}
              style={{ cursor: 'pointer' }}
            />
          </div>

          <Menu
            mode="inline"
            selectedKeys={[selected]}
            // 위에 검색 단추가 생겨 height:100% 를 주면 그만큼 아래로 넘친다.
            // Sider 가 이미 흰 바탕이라 내용 높이만큼만 그려도 빈 곳이 보이지 않는다.
            style={{ borderInlineEnd: 0 }}
            // 반드시 절대 경로로 이동한다.
            // 상대 경로(navigate('departments'))를 쓰면 React Router 가 현재
            // 경로를 기준으로 해석해 /app/users/departments 처럼 계속 덧붙는다.
            // 묶음을 누르면 펼치기만 한다. 이동은 화면 항목에서만 한다.
            onClick={({ key }) => {
              if (!key.startsWith('g:')) navigate(`/app/${key}`);
            }}
            openKeys={openKeys}
            onOpenChange={(keys) => {
              setOpenKeys(keys as string[]);
              setTouchedKeys(true);
            }}
            // 메뉴 구성은 lib/menu.tsx 한곳에 있다. 화면을 더할 때 그 표에 넣는다.
            items={buildMenu(menuContext)}
          />
        </Sider>

        <Content style={{ padding: 24, overflow: 'auto' }}>
          {/*
            화면 설명. 화면마다 심지 않고 여기 한 곳에서 띄운다.
            그래야 **모든 화면의 같은 자리**에 있고, 빠진 화면이 생기지 않는다.
            내용은 lib/guide.tsx 한 표에 모여 있다.
          */}
          <PageGuide />

          {/*
            본문만 울타리로 감싼다. 한 화면이 터져도 머리글·메뉴는 살아 있어야
            다른 화면으로 빠져나갈 수 있다. 경로가 바뀌면 울타리가 풀린다.
          */}
          <ErrorBoundary resetKey={location.pathname}>
            <Routes>
              {/* 첫 화면. 운영자는 운영 현황, 기업 관리자는 자사 현황이다. */}
              <Route index element={<Navigate to={home} replace />} />
              <Route path="dashboard" element={<TenantDashboardPage />} />
              <Route path="users" element={<UserListPage />} />
              <Route path="departments" element={<DepartmentPage />} />
              <Route path="roles" element={<RolePage />} />
              <Route path="settings" element={<TenantSettingsPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="store" element={<ProductStorePage />} />
              <Route path="billing" element={<TenantBillingPage />} />
              <Route path="logs" element={<LogsPage scope="TENANT" />} />
              <Route path="ai-reports" element={<AiReportArchivePage />} />
              {asPlatform && <Route path="admin-logs" element={<LogsPage scope="PLATFORM" />} />}
              {asPlatform && <Route path="overview" element={<DashboardPage />} />}
              {asPlatform && <Route path="products" element={<ProductPage />} />}
              {asPlatform && <Route path="tenants" element={<AdminTenantPage />} />}
              {asPlatform && <Route path="admin-billing" element={<AdminBillingPage />} />}
              {asPlatform && <Route path="payments" element={<PaymentPage />} />}
        {asPlatform && <Route path="revenue" element={<RevenuePage />} />}
              {asPlatform && <Route path="plans" element={<PlanPage />} />}
              {asPlatform && <Route path="codes" element={<CodePage />} />}
              {asPlatform && <Route path="ai" element={<AiSettingPage />} />}
              {asPlatform && <Route path="mail" element={<MailSettingPage />} />}
              {asPlatform && (
                <Route path="mail-templates" element={<MessageTemplatePage />} />
              )}
              {asPlatform && <Route path="integrations" element={<IntegrationPage />} />}
              {asPlatform && <Route path="footer" element={<FooterPage />} />}
              {asPlatform && <Route path="system" element={<SystemStatusPage />} />}
              {asPlatform && <Route path="tenant-usage" element={<TenantUsagePage />} />}
              {/* 잘못된 하위 경로는 빈 화면 대신 첫 화면으로 되돌린다. */}
              <Route path="*" element={<Navigate to={`/app/${home}`} replace />} />
            </Routes>
          </ErrorBoundary>
        </Content>
      </Layout>

      <AppFooter />
    </Layout>
  );
}

/**
 * 지금 보고 있는 화면의 설명 단추.
 *
 * 설명이 없는 화면에서는 아무것도 그리지 않는다. 빈 설명창을 띄우면
 * "눌렀는데 아무것도 없다" 가 되어 다음부터 아무도 누르지 않는다.
 */
function PageGuide() {
  const { pathname } = useLocation();
  const key = guideKeyFromPath(pathname);
  const entry = key ? GUIDE[key] : undefined;
  if (!entry) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
      <PageHelp title={entry.title} withLabel>
        {entry.body}
      </PageHelp>
    </div>
  );
}

/** 모듈 상태인 지원 세션을 React 에 연결한다. */
function useSupportSession() {
  return useSyncExternalStore(subscribeSupportSession, getSupportSession, () => null);
}

/** 모듈 상태인 기업 로고를 React 에 연결한다. */
function useCompanyLogo() {
  return useSyncExternalStore(subscribeCompanyLogo, getCompanyLogo, () => null);
}

/**
 * 지원 모드 배너.
 *
 * 항상 보여야 한다. 지금 보고 있는 것이 **남의 회사 개인정보**라는 사실과
 * 자동 종료까지 남은 시간을 숨기면, 실수로 고객 데이터를 고치게 된다.
 */
function SupportBanner() {
  const support = useSupportSession();
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [leaving, setLeaving] = useState(false);

  // 남은 시간 표시를 1분마다 갱신한다.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  if (!support) return null;

  const remaining = remainingText(support.expiresAt);

  /*
   * 지원 모드 종료.
   *
   * 화면 안에서 상태만 갈아 끼우지 않고 **주소를 새로 연다.**
   *
   * 전에는 setSupportSession(null) → /auth/refresh → setProfile → navigate 를
   * 한 함수 안에서 이어 붙였는데, 그 사이 한 번이라도 "지원 모드는 끝났지만
   * 운영자 프로필은 아직" 인 어중간한 상태가 생긴다. 그 순간 라우트 목록이
   * 통째로 바뀌면서 기업 관리 화면이 그려지지 않고 **백지**가 됐다
   * (서버 로그에 호출이 한 건도 남지 않았다).
   *
   * 지원 모드는 보안 경계를 넘는 전환이라 어중간한 상태를 둘 이유가 없다.
   * 쿠키를 지운 뒤 통째로 다시 띄우면 restoreSession() 이 운영자 세션으로
   * 깨끗하게 복구한다. 캐시도 토큰도 남지 않는다.
   */
  const leave = async () => {
    setLeaving(true);
    try {
      await apiClient.delete('/support');
    } catch {
      // 이미 끝났거나 못 끊었어도 화면은 빠져나와야 한다. 서버 세션이 남아
      // 있다면 만료 시각에 자동으로 끊긴다.
      message.warning('지원 세션 종료 요청이 실패했습니다. 화면만 빠져나옵니다.');
    }

    setSupportSession(null);
    setAccessToken(null);
    client.clear();
    window.location.assign('/app/tenants');
  };

  return (
    <Alert
      type="warning"
      banner
      showIcon
      icon={<SafetyCertificateOutlined />}
      style={{ borderRadius: 0 }}
      message={
        <span>
          <strong>지원 모드</strong> — {support.companyName} 의 데이터를 보고 있습니다.
          {remaining ? ` 자동 종료까지 ${remaining} 남았습니다.` : ' 곧 자동 종료됩니다.'}
          {' 이 세션의 모든 조회·변경은 감사로그에 기록됩니다.'}
        </span>
      }
      action={
        <Button size="small" danger loading={leaving} onClick={() => void leave()}>
          지원 모드 종료
        </Button>
      }
    />
  );
}

/**
 * 세션 존재 표시 쿠키 확인.
 *
 * Refresh 토큰은 HttpOnly 라 읽을 수 없으므로, 서버가 함께 심어 둔
 * 비밀값 아닌 표시 쿠키로 "복구를 시도할 가치가 있는지"만 판단한다.
 * 이 쿠키가 있어도 실제 인증은 서버가 HttpOnly 쿠키로 다시 검증한다.
 */
function hasSessionHint(): boolean {
  return document.cookie.split(';').some((c) => c.trim().startsWith('wnh_has_session='));
}

function LoadingScreen() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <Spin size="large" />
    </div>
  );
}
